import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Renders assistant chat messages written in GitHub-flavored markdown
 * (tables, lists, bold) as readable HTML. Used by the Parent Genie thread so
 * invoice/payment lists show as real tables and bullets, not raw asterisks.
 */
export function MarkdownMessage({ children }: { children: string }) {
  return (
    <div className="text-sm leading-relaxed break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-1 last:mb-0">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-1 last:mb-0">{children}</ol>,
          li: ({ children }) => <li className="marker:text-gray-400">{children}</li>,
          a: ({ children, href }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
              {children}
            </a>
          ),
          h1: ({ children }) => <h1 className="font-semibold text-base mb-1">{children}</h1>,
          h2: ({ children }) => <h2 className="font-semibold text-base mb-1">{children}</h2>,
          h3: ({ children }) => <h3 className="font-semibold mb-1">{children}</h3>,
          code: ({ children }) => <code className="px-1 py-0.5 bg-gray-200 rounded text-xs">{children}</code>,
          // Tables: wrap in an overflow container so wide invoice tables scroll on mobile.
          table: ({ children }) => (
            <div className="my-2 -mx-1 overflow-x-auto">
              <table className="min-w-full text-xs border-collapse">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-gray-200/70">{children}</thead>,
          th: ({ children }) => (
            <th className="border border-gray-300 px-2 py-1 text-left font-semibold whitespace-nowrap">{children}</th>
          ),
          td: ({ children }) => (
            <td className="border border-gray-200 px-2 py-1 align-top whitespace-nowrap">{children}</td>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
